import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, Input, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatStepper } from '@angular/material/stepper';

import { Subscription, firstValueFrom } from 'rxjs';

import { ApplicationStatus, MIN_ROOMS_FOR_NOMINATOR, MIN_ROOMS_FOR_VOTER, TerpecaUser } from 'src/app/models/user.model';
import { AuthService } from 'src/app/services/auth.service';
import { SettingsService } from 'src/app/services/settings.service';
import {
    ApplicationReviewData, ApplicationWarning, formatApplicationWarningsHtml, getApplicationWarnings, getApplicationWarningsForStep
} from 'src/app/utils/application.utils';
import {
    discordUsernameValidator, escapeAllGrUserIdValidator, escapeGameFrUserIdValidator, escapeRoomersUserIdValidator,
    escapeTalkNlUserIdValidator, escapeTheReviewUserIdValidator, facebookUsernameValidator, instagramUsernameValidator,
    linkedinUsernameValidator, lockMeUserIdValidator, mortyAppUsernameValidator, theEscapersUserIdValidator
} from 'src/app/utils/form.utils';

import { HomeComponent } from '../home/home.component';
import { SimpleDialogComponent } from '../simpledialog/simpledialog.component';

@Component({
  selector: 'app-apply',
  standalone: false,
  templateUrl: './apply.component.html',
  styleUrl: './apply.component.css'
})
export class ApplyComponent implements OnInit, OnDestroy {
  private auth = inject(AuthService);
  settings = inject(SettingsService);
  private dialog = inject(MatDialog);

  @Input() home: HomeComponent;
  @Input() user: TerpecaUser;
  @ViewChild('stepper') stepper: MatStepper;
  expanded = true;
  submitting = false;
  firstFormGroup: UntypedFormGroup;
  secondFormGroup: UntypedFormGroup;
  thirdFormGroup: UntypedFormGroup;
  private subscriptions: Subscription[] = [];
  Status = ApplicationStatus;
  minRoomsForVoter = MIN_ROOMS_FOR_VOTER;
  minRoomsForNominator = MIN_ROOMS_FOR_NOMINATOR;

  private defaultPrefsList: string[] = [
    'Adventure',
    'Customer service',
    'Narrative',
    'Puzzles',
    'Set design',
    'Size and scale',
    'Technology'
  ];

  ngOnInit() {
    this.expanded = this.user.status === ApplicationStatus.NONE;
    this.firstFormGroup = new UntypedFormGroup({
      realName: new UntypedFormControl('', Validators.required),
      city: new UntypedFormControl('', Validators.required),
      state: new UntypedFormControl(''),
      country: new UntypedFormControl('', Validators.required),
      appEmail: new UntypedFormControl('', [Validators.email, Validators.required]),
      facebookUsername: new UntypedFormControl('', facebookUsernameValidator),
      instagramUsername: new UntypedFormControl('', instagramUsernameValidator),
      discordUsername: new UntypedFormControl('', discordUsernameValidator),
      linkedinUsername: new UntypedFormControl('', linkedinUsernameValidator),
      bestContactMethod: new UntypedFormControl('', Validators.required),
      bio: new UntypedFormControl('', Validators.required),
    });
    this.secondFormGroup = new UntypedFormGroup({
      irlRoomCount: new UntypedFormControl('', [Validators.required, Validators.min(1)]),
      roomCountEvidence: new UntypedFormControl('', Validators.required),
      mortyAppUsername: new UntypedFormControl('', mortyAppUsernameValidator),
      escapeTheReviewUserId: new UntypedFormControl('', escapeTheReviewUserIdValidator),
      escapeAllGrUserId: new UntypedFormControl('', escapeAllGrUserIdValidator),
      escapeGameFrUserId: new UntypedFormControl('', escapeGameFrUserIdValidator),
      escapeRoomersUserId: new UntypedFormControl('', escapeRoomersUserIdValidator),
      escapeTalkNlUserId: new UntypedFormControl('', escapeTalkNlUserIdValidator),
      lockMeUserId: new UntypedFormControl('', lockMeUserIdValidator),
      theEscapersUserId: new UntypedFormControl('', theEscapersUserIdValidator),
      regionsPlayed: new UntypedFormControl('', Validators.required),
      affiliations: new UntypedFormControl(''),
      onlinePresence: new UntypedFormControl(''),
      otherExperience: new UntypedFormControl(''),
      references: new UntypedFormControl(''),
    });
    this.thirdFormGroup = new UntypedFormGroup({
      favoriteRooms: new UntypedFormControl('', Validators.required),
      horrorPreference: new UntypedFormControl('', Validators.required),
      prefsList: new UntypedFormControl([...this.defaultPrefsList], Validators.required),
    });
    this.subscriptions.push(
      this.auth.user.subscribe((value: TerpecaUser) => {
        if (value) {
          this.firstFormGroup.patchValue(value, { emitEvent: false });
          this.secondFormGroup.patchValue(value, { emitEvent: false });
          this.thirdFormGroup.patchValue(value, { emitEvent: false });
          if (this.user.status >= ApplicationStatus.VOTER) {
            if (!this.firstFormGroup.valid) {
              this.firstFormGroup.markAllAsTouched();
            }
            if (!this.secondFormGroup.valid) {
              this.secondFormGroup.markAllAsTouched();
            }
            if (!this.thirdFormGroup.valid) {
              this.thirdFormGroup.markAllAsTouched();
            }
          }
        }
      }));
  }

  ngOnDestroy() {
    this.subscriptions.forEach(subscription => { subscription.unsubscribe(); });
  }

  async prefRanksChanged(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.thirdFormGroup.value.prefsList, event.previousIndex, event.currentIndex);
    this.thirdFormGroup.value.prefsList.valid = true;
    this.thirdFormGroup.markAsDirty();
  }

  async save(formGroup: UntypedFormGroup) {
    if (!formGroup.valid) {
      return;
    }
    await this.auth.updateUser(formGroup.value);
    formGroup.markAsPristine();
  }

  async saveAndNext(formGroup: UntypedFormGroup, step: number) {
    if (!formGroup.valid) {
      return;
    }
    const proceed = await this.confirmWarnings(
      getApplicationWarningsForStep(this.getApplicationData(), step),
      'Please review this information',
      'We noticed some issues that often cause applications to be rejected. Please consider fixing them before continuing:',
      'Continue Anyway');
    if (!proceed) {
      return;
    }
    await this.save(formGroup);
    this.stepper.next();
  }

  async submit() {
    if (!this.thirdFormGroup.valid || this.submitting) {
      return;
    }
    this.submitting = true;
    try {
      const proceed = await this.confirmWarnings(
        getApplicationWarnings(this.getApplicationData()),
        'Please review your application before submitting',
        'We noticed some issues that often cause applications to be rejected. Please consider fixing them before submitting:',
        'Submit Anyway');
      if (!proceed) {
        return;
      }
      if (this.firstFormGroup.dirty) {
        await this.save(this.firstFormGroup);
      }
      if (this.secondFormGroup.dirty) {
        await this.save(this.secondFormGroup);
      }
      if (this.user.status === ApplicationStatus.NONE) {
        await this.auth.submitApplication(this.thirdFormGroup.value);
      } else if (this.user.status === ApplicationStatus.VOTER && !this.user.upgradeRequested) {
        await this.auth.requestUpgrade(this.thirdFormGroup.value);
      } else {
        await this.auth.updateUser(this.thirdFormGroup.value);
      }
      this.expanded = false;
      this.home.requestUpgrade = false;
    } finally {
      this.submitting = false;
    }
  }

  private getApplicationData(): ApplicationReviewData {
    return {
      ...this.user,
      ...this.firstFormGroup.value,
      ...this.secondFormGroup.value,
      ...this.thirdFormGroup.value,
      isUpgrade: this.user.status === ApplicationStatus.VOTER
    };
  }

  private async confirmWarnings(warnings: ApplicationWarning[], title: string, intro: string,
                                okLabel: string): Promise<boolean> {
    if (!warnings.length) {
      return true;
    }
    const result = await firstValueFrom(this.dialog.open(SimpleDialogComponent, {
      data: {
        title,
        message: `<p>${intro}</p>${formatApplicationWarningsHtml(warnings)}<p>You can go back and fix these, or continue if you believe everything is correct.</p>`,
        okLabel,
        cancelLabel: 'Go Back and Fix'
      }
    }).afterClosed());
    return Boolean(result);
  }
}
